import React, { useState } from 'react';
import { CheckIcon, TargetIcon } from 'lucide-react';
type SeniorityLevel = 'Junior' | 'Mid-Level' | 'Senior';
const levelColors: Record<SeniorityLevel, {
  main: string;
  light: string;
  border: string;
}> = {
  Junior: {
    main: '#0ea5e9',
    light: 'rgba(14,165,233,0.08)',
    border: 'rgba(14,165,233,0.2)'
  },
  'Mid-Level': {
    main: '#ff3333',
    light: 'rgba(255,51,51,0.08)',
    border: 'rgba(255,51,51,0.2)'
  },
  Senior: {
    main: '#8b5cf6',
    light: 'rgba(139,92,246,0.08)',
    border: 'rgba(139,92,246,0.2)'
  }
};
const levelContent: Record<SeniorityLevel, {
  tagline: string;
  focus: string;
  description: string;
}> = {
  Junior: {
    tagline: 'Foundational Potential',
    focus: "Verify the basics so you don't waste time on candidates who lack the building blocks.",
    description: 'Stop guessing if a candidate knows the core concepts. Our Junior Assessment identifies high-potential developers who possess the essential Laravel syntax and logic required to learn, grow, and contribute to your team from day one.'
  },
  'Mid-Level': {
    tagline: 'Productivity & Reliability',
    focus: 'Ensure they can handle the workload without constant oversight.',
    description: 'Scale your development team with confidence. Our Mid-Level Assessment evaluates architectural awareness and standard coding practices, ensuring you hire developers who can own features, write clean code, and hit the ground running with minimal supervision.'
  },
  Senior: {
    tagline: 'Strategic Expertise',
    focus: 'Verify they can solve complex, high-stakes problems.',
    description: "Don't just hire a coder; hire a lead. Our Senior Assessment probes deep into system design, security, and scalability—identifying experienced architects capable of making high-level technical decisions that protect your product and streamline your roadmap."
  }
};
const liteCheckoutLinks: Record<SeniorityLevel, string> = {
  Junior: 'https://certificationforlaravel.quadernoapp.com/checkout/link/1e91771c1130cb0da010161b4b12ed325c89f214',
  'Mid-Level': 'https://certificationforlaravel.quadernoapp.com/checkout/link/c315f4ae79554bbbe6f2a769f753d7beeb2f383c',
  Senior: 'https://certificationforlaravel.quadernoapp.com/checkout/link/39989cc6c0f6bce2d3edf11a084598160588c921'
};
const proCheckoutLinks: Record<SeniorityLevel, string> = {
  Junior: 'https://certificationforlaravel.quadernoapp.com/checkout/link/575b8ed54ad9ea520b00382643f7eca8b283c8f3',
  'Mid-Level': 'https://certificationforlaravel.quadernoapp.com/checkout/link/7de56c5f6f3c8b1063a67d7a1918a9601ef747e3',
  Senior: 'https://certificationforlaravel.quadernoapp.com/checkout/link/21dabc06127ed4589af7dba00b1f6a35db3a7bf2'
};
export function Pricing() {
  const [selectedLevel, setSelectedLevel] = useState<SeniorityLevel>('Mid-Level');
  const levels: SeniorityLevel[] = ['Junior', 'Mid-Level', 'Senior'];
  const currentContent = levelContent[selectedLevel];
  const colors = levelColors[selectedLevel];
  return <section id="pricing" className="py-12 md:py-20 bg-white border-t border-gray-100" data-id="element-250">
      <div className="container mx-auto px-4 max-w-7xl" data-id="element-251">
        <div className="text-center mb-8 sm:mb-12" data-id="element-252">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-gray-900" data-id="element-253">
            How Much Do Laravel Recruitment Tests Cost?
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto text-base sm:text-lg" data-id="element-254">
            From $11 per test. Choose the assessment level and depth that fits
            your hiring needs — no subscriptions, no hidden fees.
          </p>
        </div>

        {/* Level Selection Tabs */}
        <div className="flex justify-center mb-8 sm:mb-10" data-id="element-255">
          <div className="bg-gray-100 p-1 rounded-full inline-flex relative" data-id="element-256">
            {levels.map(level => <button key={level} onClick={() => setSelectedLevel(level)} className={`relative px-6 py-2.5 text-sm sm:text-base font-medium rounded-full transition-all duration-200 ease-in-out ${selectedLevel === level ? 'text-white shadow-sm' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200/50'}`} style={selectedLevel === level ? {
            backgroundColor: colors.main
          } : undefined} data-id="element-257">
                {level}
              </button>)}
          </div>
        </div>

        {/* Level Description */}
        <div className="max-w-3xl mx-auto text-center mb-12 sm:mb-16" data-id="element-258">
          <p className="font-semibold text-lg sm:text-xl mb-2" style={{
          color: colors.main
        }} data-id="element-259">
            "{currentContent.tagline}"
          </p>
          <p className="text-gray-900 font-medium text-sm sm:text-base mb-3" data-id="element-260">
            {currentContent.focus}
          </p>
          <p className="text-gray-600 text-sm sm:text-base leading-relaxed" data-id="element-261">
            {currentContent.description}
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto mb-16" data-id="element-262">
          {/* Lite Assessment */}
          <div className="rounded-2xl border border-gray-200 shadow-sm bg-white p-6 sm:p-8 flex flex-col" data-id="element-263">
            <div className="mb-6" data-id="element-264">
              <div className="flex justify-between items-start mb-2" data-id="element-265">
                <h3 className="text-2xl font-bold text-gray-900" data-id="element-266">
                  Lite Assessment
                </h3>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border" style={{
                backgroundColor: colors.light,
                color: colors.main,
                borderColor: colors.border
              }} data-id="element-267">
                  {selectedLevel} Test
                </span>
              </div>
              <p className="text-gray-600 text-sm sm:text-base mb-6" data-id="element-268">
                Quick screening to filter candidates.
              </p>
              <div className="flex items-baseline mb-2" data-id="element-269">
                <span className="text-4xl font-bold text-gray-900" data-id="element-270">$11</span>
                <span className="text-gray-500 ml-2 font-medium" data-id="element-271">
                  / single test
                </span>
              </div>
            </div>

            <div className="mb-8 flex-grow" data-id="element-272">
              <h4 className="font-semibold text-gray-900 mb-4 text-sm uppercase tracking-wider" data-id="element-273">
                Features included
              </h4>
              <ul className="space-y-3 mb-8" data-id="element-274">
                <li className="flex items-start bg-gray-50 p-3 rounded-lg" data-id="element-275">
                  <CheckIcon className="h-5 w-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" data-id="element-276" />
                  <span className="text-gray-700 font-medium" data-id="element-277">
                    Pass/Fail + Score %
                  </span>
                </li>
              </ul>
            </div>

            <a href={liteCheckoutLinks[selectedLevel]} className="block w-full text-center py-3 px-6 rounded-2xl border-2 font-medium transition-colors" style={{
            borderColor: colors.main,
            color: colors.main
          }} onMouseEnter={e => {
            e.currentTarget.style.backgroundColor = colors.light;
          }} onMouseLeave={e => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }} data-id="element-278">
              Buy Lite Tests
            </a>
          </div>

          {/* Professional Assessment */}
          <div className="rounded-2xl border-2 shadow-lg bg-white p-6 sm:p-8 flex flex-col relative transform md:-translate-y-2" style={{
          borderColor: colors.main
        }} data-id="element-279">
            <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-white text-xs font-bold py-1.5 px-4 rounded-full whitespace-nowrap shadow-sm" style={{
            backgroundColor: colors.main
          }} data-id="element-280">
              RECOMMENDED
            </div>

            <div className="mb-6" data-id="element-281">
              <div className="flex justify-between items-start mb-2" data-id="element-282">
                <h3 className="text-2xl font-bold text-gray-900" data-id="element-283">
                  Professional Assessment
                </h3>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border" style={{
                backgroundColor: colors.light,
                color: colors.main,
                borderColor: colors.border
              }} data-id="element-284">
                  {selectedLevel} Test
                </span>
              </div>
              <p className="text-gray-600 text-sm sm:text-base mb-6" data-id="element-285">
                Deep insights for final hiring decisions.
              </p>
              <div className="flex items-baseline mb-2" data-id="element-286">
                <span className="text-4xl font-bold text-gray-900" data-id="element-287">$19</span>
                <span className="text-gray-500 ml-2 font-medium" data-id="element-288">
                  / single test
                </span>
              </div>
            </div>

            <div className="mb-8 flex-grow" data-id="element-289">
              <h4 className="font-semibold text-gray-900 mb-4 text-sm uppercase tracking-wider" data-id="element-290">
                Features included
              </h4>
              <ul className="space-y-3 mb-8" data-id="element-291">
                <li className="flex items-start p-3 rounded-lg border" style={{
                backgroundColor: colors.light,
                borderColor: colors.border
              }} data-id="element-292">
                  <CheckIcon className="h-5 w-5 mr-3 flex-shrink-0 mt-0.5" style={{
                  color: colors.main
                }} data-id="element-293" />
                  <span className="text-gray-900 font-semibold" data-id="element-294">
                    Full Analysis + Strength/Weakness Report
                  </span>
                </li>
              </ul>
            </div>

            <a href={proCheckoutLinks[selectedLevel]} className="block w-full text-center py-3 px-6 rounded-2xl text-white font-medium shadow-sm transition-colors" style={{
            backgroundColor: colors.main
          }} onMouseEnter={e => {
            e.currentTarget.style.opacity = '0.9';
          }} onMouseLeave={e => {
            e.currentTarget.style.opacity = '1';
          }} data-id="element-295">
              Buy Professional Tests
            </a>
          </div>
        </div>

        {/* Additional Info Sections */}
        <div className="max-w-5xl mx-auto space-y-6" data-id="element-296">
          {/* Tiered Proficiency Levels */}
          <div className="bg-gray-50/80 rounded-2xl p-6 sm:p-8 border border-gray-200" data-id="element-297">
            <div className="flex items-center mb-4" data-id="element-298">
              <div className="bg-white p-2 rounded-lg shadow-sm border border-gray-100 mr-4" data-id="element-299">
                <TargetIcon className="w-6 h-6" style={{
                color: colors.main
              }} data-id="element-300" />
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900" data-id="element-301">
                Tiered Proficiency Levels
              </h3>
            </div>
            <p className="text-gray-600 mb-6 text-base sm:text-lg" data-id="element-302">
              All tests are calibrated for the specific seniority of your
              candidate:
            </p>
            <div className="grid md:grid-cols-3 gap-4 sm:gap-6" data-id="element-303">
              <div className="bg-white p-5 rounded-xl border shadow-sm transition-colors" style={{
              borderColor: selectedLevel === 'Junior' ? colors.border : '#e5e7eb'
            }} data-id="element-304">
                <h4 className="font-bold text-gray-900 mb-2 text-lg" data-id="element-305">Junior</h4>
                <p className="text-sm text-gray-600 leading-relaxed" data-id="element-306">
                  Focuses on core Laravel syntax, basic MVC understanding, and
                  standard coding practices.
                </p>
              </div>
              <div className="bg-white p-5 rounded-xl border shadow-sm transition-colors" style={{
              borderColor: selectedLevel === 'Mid-Level' ? colors.border : '#e5e7eb'
            }} data-id="element-307">
                <h4 className="font-bold text-gray-900 mb-2 text-lg" data-id="element-308">
                  Mid-Level
                </h4>
                <p className="text-sm text-gray-600 leading-relaxed" data-id="element-309">
                  Evaluates architectural awareness, database optimization, and
                  common design patterns.
                </p>
              </div>
              <div className="bg-white p-5 rounded-xl border shadow-sm transition-colors" style={{
              borderColor: selectedLevel === 'Senior' ? colors.border : '#e5e7eb'
            }} data-id="element-310">
                <h4 className="font-bold text-gray-900 mb-2 text-lg" data-id="element-311">Senior</h4>
                <p className="text-sm text-gray-600 leading-relaxed" data-id="element-312">
                  Tests deep system design, security, scalability, and complex
                  problem-solving capabilities.
                </p>
              </div>
            </div>
          </div>

          {/* Bulk Buy Savings */}
          <div className="bg-white rounded-2xl p-6 sm:p-8 border border-gray-200 shadow-sm" data-id="element-313">
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3" data-id="element-314">
              Bulk Buy Savings Explained
            </h3>
            <p className="text-gray-600 text-sm sm:text-base leading-relaxed mb-4" data-id="element-315">
              We offer a cumulative{' '}
              <strong className="text-gray-900" data-id="element-316">10% discount</strong> at every
              step (10, 20, 30 or more) to help you scale your recruitment
              process cost-effectively.
            </p>
            <p className="text-gray-600 text-sm sm:text-base leading-relaxed" data-id="element-317">
              <strong className="text-gray-900" data-id="element-318">Note:</strong> All bulk credits
              are valid for 12 months and can be applied to any seniority level
              (Junior, Mid, or Senior) as needed.{' '}
              <a href="mailto:contact@certificationforlaravel.org" className="hover:underline font-medium" style={{
              color: colors.main
            }} data-id="element-319">
                Contact us
              </a>{' '}
              for your order.
            </p>
          </div>
        </div>
      </div>
    </section>;
}